import React, { useState, useEffect } from 'react';
import Modal from '../../common/Modal';
import { useLanguage } from '../../../context/LanguageContext';
import { translateDynamic, getEntityTranslation } from '../../../context/LanguageContext';

const PaymentConfirmModal = ({
    isOpen,
    onClose,
    currentConfirmOrder,
    selectedPaymentMethod,
    selectedModalPaymentMethod,
    setSelectedModalPaymentMethod,
    paymentMethods,
    pendingTransaction,
    processTransactionWithPayment,
    selectedDiningOption,
    diningOptionObjects,
    tableNumber,
    currency,
}) => {
    const { t, language, translationDetails } = useLanguage();

    // Double-charge guard.
    //
    // This button used to be disabled only when there was nothing to charge,
    // and it called an async handler without awaiting it. Nothing on screen
    // changed while the request was in flight, so on a cold backend a cashier
    // taps again — and every tap fired a fresh charge carrying the SAME
    // prepared payload. That is how production ended up with duplicate
    // transactions 0.4-4.3s apart, identical amounts and identical stored
    // `time`, some of them triples.
    //
    // Guarded here rather than in either caller because this modal is the one
    // component every charge path in the product goes through: the native POS
    // (SellSurface, OrdersPanelNative) and the legacy till (CashierSalesPage).
    const [submitting, setSubmitting] = useState(false);

    // A reopened modal is a NEW sale. Without this, a modal left in the
    // submitting state by an unmount mid-charge would reopen permanently
    // dead, and the till could not take money at all.
    useEffect(() => {
        if (isOpen) setSubmitting(false);
    }, [isOpen]);

    // The method this tap would actually charge with. On the confirm-order
    // path it is fixed by the order; otherwise the cashier picks it in this
    // modal, and it starts null.
    const effectiveMethod = currentConfirmOrder ? selectedPaymentMethod : selectedModalPaymentMethod;

    const handleCharge = async () => {
        // Belt to the disabled attribute's braces: a tap can still land in
        // the frame before React re-renders the disabled state.
        if (submitting) return;
        // Never latch `submitting` for a tap that cannot charge. The charge
        // flow returns early on a missing method WITHOUT closing this modal,
        // so latching here left the button permanently greyed and the sale
        // impossible to complete — worse than the duplicate this guard
        // exists to prevent. The disabled attribute below covers the same
        // case; this is the belt to its braces.
        if (!effectiveMethod) return;
        setSubmitting(true);
        try {
            await processTransactionWithPayment(effectiveMethod);
            // Deliberately NOT cleared on success. The caller closes this
            // modal, and the gap between the charge landing and the close
            // rendering is exactly where a second tap used to get through.
        } catch (err) {
            // A failed charge must leave the till able to retry — the
            // customer is still standing there.
            //
            // Swallowed rather than re-thrown: this is an onClick handler, so
            // a rejection here has no caller to catch it and would surface as
            // an unhandled promise rejection. The charge path does its own
            // error reporting to the cashier (transactionFlow's catch sets the
            // on-screen error); this only owns re-arming the button.
            setSubmitting(false);
            console.error('Payment failed, button re-armed for retry:', err);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={t('confirm_payment')}
            footer={(
                <>
                    <button
                        type="button"
                        className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        onClick={onClose}
                        // Cancelling mid-charge re-armed the guard while the
                        // cart and pendingTransaction survived, so reopening
                        // and charging again produced a second transaction
                        // with the same payload. Closing the escape hatch
                        // while money is moving is the point.
                        disabled={submitting}
                    >
                        {t('cancel')}
                    </button>
                    <button
                        type="button"
                        className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-gray-300 disabled:cursor-not-allowed"
                        data-testid="process-payment"
                        onClick={handleCharge}
                        disabled={!pendingTransaction || submitting || !effectiveMethod}
                    >
                        {submitting ? t('processing_payment') : t('process_payment')}
                    </button>
                </>
            )}
        >
            <div className="space-y-4">
                {currentConfirmOrder ? (
                    <p>{t('please_confirm_payment_for')} {getEntityTranslation(diningOptionObjects[selectedDiningOption], language, translationDetails) || translateDynamic(selectedDiningOption, t)} - {tableNumber} {t('with')} <strong>{translateDynamic(selectedPaymentMethod, t)}</strong>:</p>
                ) : (
                    <>
                        <p>{t('select_payment_method_complete')}:</p>
                        <div className="grid grid-cols-2 gap-3 mt-4">
                            {paymentMethods.map(method => (
                                <button
                                    key={method}
                                    onClick={() => setSelectedModalPaymentMethod(method)}
                                    className={`flex items-center justify-center px-4 py-3 border ${selectedModalPaymentMethod === method
                                        ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-500 ring-opacity-50'
                                        : 'border-gray-300 bg-white hover:bg-gray-50'
                                        } rounded-md shadow-sm text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500`}
                                >
                                    {translateDynamic(method, t)}
                                </button>
                            ))}
                        </div>
                    </>
                )}

                {selectedModalPaymentMethod === 'Split Payment' && (
                    <div className="mt-4 pt-3 border-t">
                        <p className="text-sm text-gray-600">
                            {t('split_payment_configure')}
                        </p>
                    </div>
                )}

                {pendingTransaction && (
                    <div className="mt-6 pt-4 border-t">
                        <div className="text-sm font-medium mb-2">{t('order_summary')}:</div>
                        <div className="flex justify-between text-sm">
                            <span>{t('subtotal')}</span>
                            <span>{currency}{pendingTransaction.total_amount.toFixed(2)}</span>
                        </div>
                        {pendingTransaction.discount_amount > 0 && (
                            <div className="flex justify-between text-sm">
                                <span>{t('discount')}:</span>
                                <span>{currency}{pendingTransaction.discount_amount.toFixed(2)}</span>
                            </div>
                        )}
                        <div className="flex justify-between font-medium mt-1">
                            <span>{t('total')}</span>
                            <span>{currency}{pendingTransaction.final_amount.toFixed(2)}</span>
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default PaymentConfirmModal;
